import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Phone, MessageCircle, Mail, MapPin, ExternalLink, ChevronDown,
  ShieldCheck, Leaf, Heart, Facebook, Instagram, Youtube,
} from 'lucide-react';
import { SERVICES } from '../data/servicesData';
import { PRODUCTS } from '../data/productsData';

const PHONE_CALL = '+917819988010';
const PHONE_WA = '919410110910';
const EMAIL = 'official.entorid@gmail.com';
const MAPS_URL = 'https://maps.app.goo.gl/yFdekDFpNCGpNfvb6';
const ADDRESS = 'Weblan Building, IT Park, 45 B, Sahastradhara Road, Near L.I.C Office, Doon IT Park, Govind Vihar, Dehradun, Uttarakhand – 248013';

const QUICK_SERVICES = [
  { slug: 'cockroach-control', label: 'Cockroach Control' },
  { slug: 'anti-termite-treatment', label: 'Termite Treatment' },
  { slug: 'wood-borer', label: 'Bed Bug & Wood Borer' },
  { slug: 'mosquito-fogging', label: 'Mosquito Control' },
  { slug: 'rodent-control', label: 'Rodent Control' },
  { slug: 'residential', label: 'General Pest Control' },
];

// Top product categories — all link to /products page (coming soon).
const QUICK_PRODUCTS = (PRODUCTS || []).slice(0, 6).map(p => ({ id: p.id, label: p.name }));

const TRUST_BADGES = [
  { Icon: ShieldCheck, label: 'Certified Professionals' },
  { Icon: Leaf, label: 'Safe & Eco-Friendly' },
  { Icon: Heart, label: '100% Customer Satisfaction' },
];

// WhatsApp brand SVG
const WhatsAppGlyph = ({ size = 18 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-hidden>
    <path d="M20.52 3.48A11.94 11.94 0 0012 0C5.37 0 0 5.37 0 12a11.94 11.94 0 001.64 6l-1.72 6.3 6.47-1.7A11.94 11.94 0 0012 24c6.63 0 12-5.37 12-12a11.94 11.94 0 00-3.48-8.52zM12 21.82a9.83 9.83 0 01-5-1.36l-.36-.21-3.85 1.02 1.03-3.76-.23-.38A9.82 9.82 0 1121.82 12 9.83 9.83 0 0112 21.82z"/>
  </svg>
);

const SOCIALS = [
  { Icon: Facebook, href: 'https://www.facebook.com/profile.php?id=61582096927776', label: 'Facebook', hover: 'hover:bg-[#1877F2]' },
  { Icon: Instagram, href: 'https://www.instagram.com/ento_rid?igsh=MXd2bjg1NHBobzBxZQ==', label: 'Instagram', hover: 'hover:bg-gradient-to-br hover:from-[#f09433] hover:to-[#bc1888]' },
  { Icon: WhatsAppGlyph, href: `https://api.whatsapp.com/send?phone=${PHONE_WA}`, label: 'WhatsApp', hover: 'hover:bg-[#25D366]' },
  { Icon: Youtube, href: 'https://www.youtube.com/@EntoRid', label: 'YouTube', hover: 'hover:bg-[#FF0000]' },
];

export default function SiteFooter() {
  return (
    <footer className="bg-brand-blue-dark text-white/85" data-testid="footer" style={{ fontFamily: 'Poppins, Inter, sans-serif' }}>
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 pt-12 sm:pt-16 pb-6">
        {/* ===== Row 1 — Brand · Services · Contact ===== */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-10">
          {/* Col 1 — Company */}
          <div className="md:col-span-4">
            <Link to="/" className="inline-block">
              <div className="text-3xl font-bold text-brand-green-light tracking-tight" style={{ fontFamily: 'Poppins, sans-serif' }}>
                EntoRid
              </div>
              <div className="text-xs uppercase tracking-[0.18em] text-white/60 mt-1">Pest Control Experts</div>
            </Link>
            <p className="mt-5 text-sm leading-relaxed text-white/70">
              EntoRid is a trusted pest control service provider offering safe, effective, and affordable solutions for homes and businesses across Uttarakhand.
            </p>
            <ul className="mt-5 space-y-2.5">
              {TRUST_BADGES.map(({ Icon, label }) => (
                <li key={label} className="flex items-center gap-2.5 text-sm text-white/85">
                  <span className="w-7 h-7 rounded-full bg-brand-green/20 text-brand-green-light inline-flex items-center justify-center shrink-0">
                    <Icon size={14} />
                  </span>
                  <span>{label}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Col 2 — Services */}
          <FooterColumn id="services" title="Our Services" className="md:col-span-4">
            <ul className="space-y-2 text-sm">
              {QUICK_SERVICES.map(s => (
                <li key={s.slug}>
                  <Link
                    to={`/services/${s.slug}`}
                    className="text-white/70 hover:text-brand-green-light transition-colors inline-flex items-center gap-1"
                    data-testid={`footer-service-${s.slug}`}
                  >
                    <span className="w-1 h-1 rounded-full bg-brand-green-light/50" />
                    {s.label}
                  </Link>
                </li>
              ))}
              <li className="pt-1">
                <Link to="/services" className="text-brand-green-light text-xs font-medium hover:underline">
                  View all services →
                </Link>
              </li>
            </ul>
          </FooterColumn>

          {/* Col 3 — Contact */}
          <FooterColumn id="contact" title="Contact" className="md:col-span-4">
            <ul className="space-y-3 text-sm">
              <li>
                <a href={`tel:${PHONE_CALL}`} className="flex items-start gap-3 text-white/80 hover:text-brand-green-light transition-colors group" data-testid="footer-call">
                  <span className="w-8 h-8 rounded-full bg-white/8 inline-flex items-center justify-center shrink-0 group-hover:bg-brand-green/20 transition-colors">
                    <Phone size={14} />
                  </span>
                  <span>
                    <span className="block text-[11px] uppercase tracking-wider text-white/45">Call Us</span>
                    <span className="font-medium">+91 78199 88010</span>
                  </span>
                </a>
              </li>
              <li>
                <a href={`https://api.whatsapp.com/send?phone=${PHONE_WA}`} target="_blank" rel="noreferrer" className="flex items-start gap-3 text-white/80 hover:text-brand-green-light transition-colors group" data-testid="footer-wa">
                  <span className="w-8 h-8 rounded-full bg-white/8 inline-flex items-center justify-center shrink-0 group-hover:bg-[#25D366]/30 transition-colors">
                    <MessageCircle size={14} />
                  </span>
                  <span>
                    <span className="block text-[11px] uppercase tracking-wider text-white/45">WhatsApp</span>
                    <span className="font-medium">+91 94101 10910</span>
                  </span>
                </a>
              </li>
              <li>
                <a href={`mailto:${EMAIL}`} className="flex items-start gap-3 text-white/80 hover:text-brand-green-light transition-colors group break-all" data-testid="footer-email">
                  <span className="w-8 h-8 rounded-full bg-white/8 inline-flex items-center justify-center shrink-0 group-hover:bg-brand-green/20 transition-colors">
                    <Mail size={14} />
                  </span>
                  <span>
                    <span className="block text-[11px] uppercase tracking-wider text-white/45">Email</span>
                    <span className="font-medium">{EMAIL}</span>
                  </span>
                </a>
              </li>
              <li>
                <a href={MAPS_URL} target="_blank" rel="noreferrer" className="flex items-start gap-3 text-white/80 hover:text-brand-green-light transition-colors group" data-testid="footer-address">
                  <span className="w-8 h-8 rounded-full bg-white/8 inline-flex items-center justify-center shrink-0 group-hover:bg-brand-green/20 transition-colors">
                    <MapPin size={14} />
                  </span>
                  <span>
                    <span className="block text-[11px] uppercase tracking-wider text-white/45">Head Office</span>
                    <span className="text-[13px] leading-relaxed">{ADDRESS}</span>
                    <span className="mt-1.5 text-xs font-medium text-brand-green-light inline-flex items-center gap-1">
                      Open in Google Maps <ExternalLink size={11} />
                    </span>
                  </span>
                </a>
              </li>
            </ul>
          </FooterColumn>
        </div>

        {/* ===== Row 2 — Our Products · Legal · Follow on Social Media ===== */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-10 mt-10 md:mt-12 pt-8 md:pt-10 border-t border-white/10">
          {/* Col 1 — Products */}
          <FooterColumn id="products" title="Our Products" className="md:col-span-4">
            <ul className="space-y-2 text-sm">
              {QUICK_PRODUCTS.map((p) => (
                <li key={p.id}>
                  <Link
                    to="/products"
                    className="text-white/70 hover:text-brand-green-light transition-colors inline-flex items-center gap-1 group"
                    data-testid={`footer-product-${p.id}`}
                  >
                    <span className="w-1 h-1 rounded-full bg-amber-300/70" />
                    <span className="truncate max-w-[180px]">{p.label}</span>
                    <span className="text-[9px] uppercase font-bold tracking-wider text-amber-300/90 bg-amber-400/15 border border-amber-300/30 px-1.5 py-0.5 rounded-full whitespace-nowrap ml-1 group-hover:bg-amber-400/25 transition-colors">Soon</span>
                  </Link>
                </li>
              ))}
              <li className="pt-1">
                <Link to="/products" className="text-brand-green-light text-xs font-medium hover:underline" data-testid="footer-products-view-all">
                  View all products →
                </Link>
              </li>
            </ul>
          </FooterColumn>

          {/* Col 2 — Legal (Admin link removed) */}
          <FooterColumn id="legal" title="Legal" className="md:col-span-4">
            <ul className="space-y-2 text-sm">
              <li><Link to="/about-me" className="text-white/70 hover:text-brand-green-light" data-testid="footer-about">About Us</Link></li>
              <li><Link to="/contact" className="text-white/70 hover:text-brand-green-light" data-testid="footer-contact">Contact</Link></li>
              <li><Link to="/reviews" className="text-white/70 hover:text-brand-green-light" data-testid="footer-reviews">Reviews</Link></li>
              <li><Link to="/privacy-policy" className="text-white/70 hover:text-brand-green-light" data-testid="footer-privacy">Privacy Policy</Link></li>
              <li><Link to="/terms-conditions" className="text-white/70 hover:text-brand-green-light" data-testid="footer-terms">Terms &amp; Conditions</Link></li>
              <li><Link to="/refund-policy" className="text-white/70 hover:text-brand-green-light" data-testid="footer-refund">Refund Policy</Link></li>
            </ul>
          </FooterColumn>

          {/* Col 3 — Social (TM removed) */}
          <FooterColumn id="social" title="Follow EntoRid on Social Media" className="md:col-span-4">
            <p className="text-xs text-white/60 mb-3">Stay connected for tips, offers &amp; service updates</p>
            <div className="flex flex-row gap-3 flex-wrap" data-testid="footer-socials">
              {SOCIALS.map(({ Icon, href, label, hover }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={label}
                  data-testid={`footer-social-${label.toLowerCase()}`}
                  className={`w-10 h-10 rounded-full bg-white/8 text-white/85 inline-flex items-center justify-center transition-all duration-200 hover:scale-110 hover:text-white ${hover}`}
                >
                  <Icon size={18} />
                </a>
              ))}
            </div>
            {/* Discreet Official Access links — website + Android-shared production admin */}
            <div className="flex flex-col items-start gap-1.5 mt-6" data-testid="footer-official-links">
              <Link
                to="/official"
                data-testid="footer-official-access"
                className="inline-flex items-center gap-1.5 text-[11px] text-white/35 hover:text-brand-green-light transition-colors tracking-wide"
              >
                <span className="w-1 h-1 rounded-full bg-white/25" />
                Official Access
              </Link>
              <a
                href="https://entorid.com/official/admin"
                data-testid="footer-app-official-access"
                className="inline-flex items-center gap-1.5 text-[11px] text-white/35 hover:text-brand-green-light transition-colors tracking-wide"
              >
                <span className="w-1 h-1 rounded-full bg-white/25" />
                App Official Access
              </a>
            </div>
          </FooterColumn>
        </div>

        {/* Divider */}
        <div className="border-t border-white/10 mt-12 mb-5" />

        {/* Bottom bar */}
        <div className="flex flex-col sm:flex-row gap-3 justify-between items-center text-xs text-white/55">
          <div>&copy; {new Date().getFullYear()} EntoRid. All Rights Reserved.</div>
          <div className="text-center sm:text-right">Designed for premium pest control services</div>
        </div>
      </div>
    </footer>
  );
}

/**
 * FooterColumn — desktop: regular column. Mobile: collapsible accordion.
 */
function FooterColumn({ id, title, children, className = '' }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={className} data-testid={`footer-col-${id}`}>
      {/* Mobile accordion header */}
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="md:hidden w-full flex items-center justify-between py-3 border-b border-white/10 text-left text-white font-semibold text-sm uppercase tracking-wider"
        aria-expanded={open}
      >
        {title}
        <ChevronDown size={18} className={`text-white/60 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {/* Desktop title */}
      <h4 className="hidden md:block text-white font-semibold mb-4 text-sm uppercase tracking-wider">{title}</h4>
      {/* Body */}
      <div className={`${open ? 'block py-3' : 'hidden'} md:block`}>
        {children}
      </div>
    </div>
  );
}
